import {
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Link,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { Link as RouterLink } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Dialog } from '@/components/AppDialog'
import { useMemo, useState } from 'react'
import { api } from '@/api/client'
import { useCustomer } from '@/api/hooks'
import type { OrderSummary, PageResponse, Payment } from '@/api/types'
import {
  EmptyState,
  ErrorBanner,
  formatDate,
  formatDateTime,
  formatMoney,
  humanise,
  StatusChip,
} from '@/components/common'

/** What every note carried over from the old billing system starts with. */
const IMPORTED = '[Legacy import]'

/** Enough to read without scrolling for ever; the search narrows the rest. */
const SHOWN = 200

type HistoryTab = 'orders' | 'receipts' | 'notes'

/**
 * Everything a customer has had from us, newest first: their orders, the
 * receipts they paid, and what was written down against them.
 *
 * <p>Orders and receipts carried over from ECEEBILL are among them as orders
 * and receipts like any other; notes are what the office typed and the old
 * history kept as notes. A branch can have hundreds of each, so every tab has
 * a search.</p>
 */
export function CustomerHistoryDialog({
  customerId,
  customerName,
  onClose,
}: {
  customerId: string | null
  customerName?: string
  onClose: () => void
}) {
  const open = Boolean(customerId)
  const [tab, setTab] = useState<HistoryTab>('orders')
  const [search, setSearch] = useState('')

  const customer = useCustomer(customerId ?? undefined)
  const orders = useQuery({
    queryKey: ['customer-history-orders', customerId],
    queryFn: () =>
      api.get<PageResponse<OrderSummary>>('/api/v1/orders', { customerId: customerId ?? '', size: 200 }),
    enabled: open,
  })
  const receipts = useQuery({
    queryKey: ['customer-history-receipts', customerId],
    queryFn: () =>
      api.get<PageResponse<Payment>>('/api/v1/payments', { customerId: customerId ?? '', size: 200 }),
    enabled: open,
  })

  const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean)
  const matches = (text: string) => words.every((word) => text.toLowerCase().includes(word))

  const orderRows = useMemo(
    () =>
      [...(orders.data?.content ?? [])]
        .sort((a, b) => (b.eventDate ?? '').localeCompare(a.eventDate ?? ''))
        .filter((order) =>
          matches(`${order.orderNumber} ${order.eventDate} ${order.serviceType} ${order.status} ${formatDate(order.eventDate)}`),
        ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orders.data, search],
  )
  const receiptRows = useMemo(
    () =>
      [...(receipts.data?.content ?? [])]
        .sort((a, b) => (b.paymentDate ?? '').localeCompare(a.paymentDate ?? ''))
        .filter((receipt) =>
          matches(`${receipt.receiptNumber} ${receipt.orderNumber ?? ''} ${receipt.paymentMode} ${formatDate(receipt.paymentDate)}`),
        ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [receipts.data, search],
  )
  const notes = customer.data?.notesHistory ?? []
  const noteRows = useMemo(
    () => notes.filter((note) => matches(note.note)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [notes, search],
  )

  const counts = {
    orders: orders.data?.totalElements ?? 0,
    receipts: receipts.data?.totalElements ?? 0,
    notes: notes.length,
  }
  const loading = tab === 'orders' ? orders.isLoading : tab === 'receipts' ? receipts.isLoading : customer.isLoading
  const error = tab === 'orders' ? orders.error : tab === 'receipts' ? receipts.error : customer.error

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>History for {customerName}</DialogTitle>
      <Tabs value={tab} onChange={(_event, value) => setTab(value)} sx={{ px: 2 }}>
        <Tab value="orders" label={`Orders (${counts.orders})`} />
        <Tab value="receipts" label={`Receipts (${counts.receipts})`} />
        <Tab value="notes" label={`Notes (${counts.notes})`} />
      </Tabs>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <TextField
            size="small"
            fullWidth
            placeholder={
              tab === 'notes'
                ? 'Find a bill number, an item or a date - e.g. PPS/1075, laddu, Mar 2026'
                : 'Find a number, a date or a status'
            }
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <ErrorBanner error={error} />
          {loading ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={28} />
            </Stack>
          ) : tab === 'orders' ? (
            orderRows.length === 0 ? (
              <EmptyState message={counts.orders === 0 ? 'No orders yet' : 'No order matches'} />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Paid</TableCell>
                    <TableCell align="right">Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderRows.slice(0, SHOWN).map((order) => (
                    <TableRow key={order.id} hover>
                      <TableCell>
                        <Link component={RouterLink} to={`/orders/${order.id}`} onClick={onClose}>
                          {order.orderNumber}
                        </Link>
                      </TableCell>
                      <TableCell>{formatDate(order.eventDate)}</TableCell>
                      <TableCell>{humanise(order.serviceType)}</TableCell>
                      <TableCell>
                        <StatusChip status={order.status} />
                      </TableCell>
                      <TableCell align="right">{formatMoney(order.totalAmount)}</TableCell>
                      <TableCell align="right">{formatMoney(order.paidAmount)}</TableCell>
                      <TableCell align="right">{formatMoney(order.balanceAmount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          ) : tab === 'receipts' ? (
            receiptRows.length === 0 ? (
              <EmptyState message={counts.receipts === 0 ? 'No receipts yet' : 'No receipt matches'} />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Receipt</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell>For order</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {receiptRows.slice(0, SHOWN).map((receipt) => (
                    <TableRow key={receipt.id} hover>
                      <TableCell>{receipt.receiptNumber}</TableCell>
                      <TableCell>{formatDate(receipt.paymentDate)}</TableCell>
                      <TableCell>{humanise(receipt.paymentMode)}</TableCell>
                      <TableCell>
                        {receipt.orderId ? (
                          <Link component={RouterLink} to={`/orders/${receipt.orderId}`} onClick={onClose}>
                            {receipt.orderNumber}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell align="right">{formatMoney(receipt.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          ) : noteRows.length === 0 ? (
            <EmptyState
              message={notes.length === 0 ? 'Nothing written down yet' : 'No note matches'}
              hint={notes.length === 0 ? 'Notes and carried-over history appear here.' : undefined}
            />
          ) : (
            noteRows.slice(0, SHOWN).map((note) => {
              const imported = note.note.startsWith(IMPORTED)
              return (
                <Box
                  key={note.id}
                  sx={{ borderLeft: 3, borderColor: imported ? 'info.light' : 'primary.main', pl: 1.5, py: 0.5 }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                    {imported ? <Chip size="small" variant="outlined" label="From ECEEBILL" /> : null}
                    <Typography variant="caption" color="text.secondary">
                      Recorded {formatDateTime(note.createdAt)}
                    </Typography>
                  </Stack>
                  <Typography variant="body2">
                    {imported ? note.note.slice(IMPORTED.length).trim() : note.note}
                  </Typography>
                </Box>
              )
            })
          )}
          {(tab === 'orders' && counts.orders > SHOWN) || (tab === 'receipts' && counts.receipts > SHOWN) ? (
            <Typography variant="caption" color="text.secondary">
              Showing the latest {SHOWN}; search to narrow them down.
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
